import { useCallback } from "react";

import {
	useCreateCampaignPinnedTabMutation,
	useDeleteCampaignPinnedTabMutation,
	useGetCampaignPinnedTabsQuery,
	useUpdateCampaignPinnedTabMutation,
} from "@/pages/Private/redux/listOverviewPinnedTab/listOverviewPinnedTab.api";

import {
	CampaignPinnedFilterTab,
	CampaignPinnedTabFilters,
} from "../utils/campaignPinnedFilterTabs";

/**
 * Per-user pinned Campaigns overview filter tabs, persisted on the backend
 * (`/list-overview-pinned-tabs?scope=campaign`) so they sync across devices. The Campaigns sibling
 * of `useListOverviewPinnedTabs`: same CRUD surface, same promise-returning mutations so callers
 * can toast on success/failure — only the scope and the filter shape differ.
 */
export const useCampaignsPinnedTabs = () => {
	const { data } = useGetCampaignPinnedTabsQuery();
	const [createPinnedTab] = useCreateCampaignPinnedTabMutation();
	const [updatePinnedTab] = useUpdateCampaignPinnedTabMutation();
	const [deletePinnedTab] = useDeleteCampaignPinnedTabMutation();

	const pinnedTabs: CampaignPinnedFilterTab[] = data?.data ?? [];

	const addPinnedTab = useCallback(
		(name: string, filters: CampaignPinnedTabFilters) =>
			createPinnedTab({ name, filters }).unwrap(),
		[createPinnedTab]
	);

	const renamePinnedTab = useCallback(
		(id: number, name: string) => updatePinnedTab({ id, name }).unwrap(),
		[updatePinnedTab]
	);

	const updatePinnedTabFilters = useCallback(
		(id: number, filters: CampaignPinnedTabFilters) => updatePinnedTab({ id, filters }).unwrap(),
		[updatePinnedTab]
	);

	const removePinnedTab = useCallback(
		(id: number) => deletePinnedTab(id).unwrap(),
		[deletePinnedTab]
	);

	return {
		pinnedTabs,
		addPinnedTab,
		renamePinnedTab,
		updatePinnedTabFilters,
		removePinnedTab,
	};
};
